import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { DomainEntityBase, DomainEntityType } from '@models';
import {
  DefaultDataService,
  DefaultDataServiceConfig,
  EntityCollectionServiceElementsFactory,
  EntityDataService,
  EntityDefinitionService,
  EntityMetadata,
  HttpUrlGenerator,
} from '@ngrx/data';
import { DataCollectionService } from '../collection.service';
import { ngrxDataMetadataMap } from '../metadata';
import { AppEntityService } from './entity.service';

export class NgrxDataProviderHelper {
  public static dataServiceUrlConfig: DefaultDataServiceConfig = {
    root: `/`,
    timeout: 120000,
  };

  static registerDefaultProvider<T extends DomainEntityBase>(
    entityName: DomainEntityType,
    config?: DefaultDataServiceConfig,
    metadata?: Partial<EntityMetadata>
  ): DataCollectionService<T> {
    NgrxDataProviderHelper.registerMetadata(entityName, metadata);
    NgrxDataProviderHelper.registerDefaultDataService(
      entityName,
      config || this.dataServiceUrlConfig
    );

    return NgrxDataProviderHelper.registerDefaultCollectionService(entityName);
  }

  static registerMetadata(
    entityName: DomainEntityType,
    metadata?: Partial<EntityMetadata>
  ): void {
    const entityDefinitionService = inject(EntityDefinitionService);

    try {
      entityDefinitionService.getDefinition(entityName.toString());
    } catch {
      entityDefinitionService.registerMetadataMap({
        [entityName]:
          metadata ??
          (ngrxDataMetadataMap[entityName] as Partial<EntityMetadata<any, {}>>),
      });
    }
  }

  static registerDefaultDataService(
    entityName: DomainEntityType,
    config: DefaultDataServiceConfig
  ): void {
    const { entityDataService, httpUrlGenerator, http } =
      NgrxDataProviderHelper._getProviderDependencies();

    entityDataService.registerService(
      entityName.toString(),
      new DefaultDataService(
        entityName.toString(),
        http,
        httpUrlGenerator,
        config
      )
    );
  }

  static registerDefaultCollectionService = <T extends DomainEntityBase>(
    entityName: DomainEntityType
  ): DataCollectionService<T> => {
    const entityService = inject(AppEntityService);
    const elementsFactory = inject(EntityCollectionServiceElementsFactory);

    if (!entityService.isServiceLoaded(entityName)) {
      entityService.registerEntityCollectionService(
        new DataCollectionService(entityName, elementsFactory)
      );
    }

    return entityService.getEntityCollectionService<T>(entityName.toString());
  };

  private static _getProviderDependencies(): {
    http: HttpClient;
    httpUrlGenerator: HttpUrlGenerator;
    entityDataService: EntityDataService;
    entityDefinitionService: EntityDefinitionService;
    entityService: AppEntityService;
    elementsFactory: EntityCollectionServiceElementsFactory;
  } {
    const entityDataService = inject(EntityDataService);
    const http = inject(HttpClient);
    const httpUrlGenerator = inject(HttpUrlGenerator);
    const entityDefinitionService = inject(EntityDefinitionService);
    const entityService = inject(AppEntityService);
    const elementsFactory = inject(EntityCollectionServiceElementsFactory);

    return {
      http,
      httpUrlGenerator,
      entityDataService,
      entityDefinitionService,
      entityService,
      elementsFactory,
    };
  }
}
